// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SuperadminService } from "@/services/superadmin";
import type { LegalDocumentType, SuperadminLegalVersion } from "@/types/superadmin";

export const Route = createFileRoute("/superadmin/legal")({
  component: SuperadminLegalPage,
});

function LegalVersionTable({
  title,
  documentType,
  rows,
  onMakeCurrent,
  isUpdating,
}: {
  title: string;
  documentType: LegalDocumentType;
  rows: SuperadminLegalVersion[];
  onMakeCurrent: (documentType: LegalDocumentType, version: string) => void;
  isUpdating: boolean;
}) {
  const { t, i18n } = useTranslation("superadmin");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("legal.version")}</TableHead>
              <TableHead>{t("legal.effectiveFrom")}</TableHead>
              <TableHead>{t("legal.status")}</TableHead>
              <TableHead className="text-end">{t("common.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="font-medium">{row.version}</TableCell>
                <TableCell>
                  {new Intl.DateTimeFormat(i18n.language, {
                    dateStyle: "medium",
                  }).format(new Date(row.effectiveFrom))}
                </TableCell>
                <TableCell>
                  {row.isCurrent ? (
                    <Badge variant="outline" className="gap-1.5">
                      <CheckCircle className="h-3 w-3" />
                      {t("legal.current")}
                    </Badge>
                  ) : (
                    <Badge variant="secondary">{t("legal.archived")}</Badge>
                  )}
                </TableCell>
                <TableCell className="text-end">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={row.isCurrent || isUpdating}
                    onClick={() => onMakeCurrent(documentType, row.version)}
                  >
                    {t("legal.makeCurrent")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function SuperadminLegalPage() {
  const { t } = useTranslation("superadmin");
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["superadmin", "legal-versions"],
    queryFn: SuperadminService.listLegalVersions,
  });

  const makeCurrentMutation = useMutation({
    mutationFn: SuperadminService.makeLegalCurrent,
    onSuccess: async () => {
      toast.success(t("legal.makeCurrentSuccess"));
      await queryClient.invalidateQueries({
        queryKey: ["superadmin", "legal-versions"],
      });
    },
    onError: () => toast.error(t("legal.makeCurrentError")),
  });

  if (isLoading) {
    return (
      <div className="flex min-h-80 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("legal.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("legal.description")}
        </p>
      </div>

      <LegalVersionTable
        title={t("legal.privacyTitle")}
        documentType="privacy"
        rows={data?.privacy ?? []}
        isUpdating={makeCurrentMutation.isPending}
        onMakeCurrent={(documentType, version) =>
          makeCurrentMutation.mutate({ documentType, version })
        }
      />
      <LegalVersionTable
        title={t("legal.termsTitle")}
        documentType="terms"
        rows={data?.terms ?? []}
        isUpdating={makeCurrentMutation.isPending}
        onMakeCurrent={(documentType, version) =>
          makeCurrentMutation.mutate({ documentType, version })
        }
      />
    </div>
  );
}
